import { jsonErrorCheck2, deleteEmptyElementsInObjectRecursive } from './ResourceFunctions';
import { generateCitation } from './DataEntryFormFunctions';
import { sectionCodeAcceptability, sectionCodeCertaintyOfEvidence, sectionCodeCompetingInterests, sectionCodeCostEffectiveness, sectionCodeCosts, sectionCodeDesirableEffects, sectionCodeDirectionOfRecommendation, sectionCodeDiscussion, sectionCodeEquity, sectionCodeEvidence, sectionCodeFeasibility, sectionCodeJudgments, sectionCodeJustification, sectionCodeNetEffect, sectionCodePreferences, sectionCodeProblemImportance, sectionCodeRatings, sectionCodeRatingSystem, sectionCodeStrengthOfRecommendation, sectionCodeSummaryOfFindings, sectionCodeUndesirableEffects } from './CodeSystemLookup';

const reorderRecommendationJustificationContent = (content) => {
  let reorderedContent = [
    {
      "type": sectionCodeRatingSystem
    },
    {
      "type": sectionCodeStrengthOfRecommendation
    },
    {
      "type": sectionCodeDirectionOfRecommendation
    },
    {
      "type": sectionCodeRatings
    },
    {
      "type": sectionCodeDiscussion
    },
    {
      "type": sectionCodeEvidence,
      "component": [
        {
          "type": sectionCodeSummaryOfFindings
        },
        {
          "type": sectionCodeDesirableEffects
        },
        {
          "type": sectionCodeUndesirableEffects
        },
        {
          "type": sectionCodeDiscussion
        }
      ]
    },
    {
      "type": sectionCodeNetEffect,
      "component": [
        {
          "type": sectionCodePreferences
        },
        {
          "type": sectionCodeDiscussion
        }
      ]
    },
    {
      "type": sectionCodeJudgments,
      "component": [
        {
          "type": sectionCodeJustification
        },
        {
          "type": sectionCodeProblemImportance
        },
        {
          "type": sectionCodeCosts
        },
        {
          "type": sectionCodeCostEffectiveness
        },
        {
          "type": sectionCodeEquity
        },
        {
          "type": sectionCodeAcceptability
        },
        {
          "type": sectionCodeFeasibility
        }
      ]
    },
    {
      "type": sectionCodeCompetingInterests
    }
  ];
  for (const item of content) {
    const typeCode = item.type?.coding?.[0]?.code;
    if (typeCode) {
      switch (typeCode) {
        case "rating-system":
          reorderedContent[0] = item;
          break;
        case "strength-of-recommendation":
          reorderedContent[1] = item;
          break;
        case "direction-of-recommendation":
          reorderedContent[2] = item;
          break;
        case "ratings":
          reorderedContent[3] = item;
          break;
        case "discussion":
          reorderedContent[4] = item;
          break;
        case "recommendation-rating":
          if (item.component) {
            for (const part of item.component) {
              const partCode = part.type?.coding?.[0]?.code;
              if (partCode === "rating-system") {
                reorderedContent[0] = part;
              } else if (partCode === "strength-of-recommendation") {
                reorderedContent[1] = part;
              } else if (partCode === "direction-of-recommendation") {
                reorderedContent[2] = part;
              } else if (partCode === "ratings") {
                reorderedContent[3] = part;
              } else if (partCode === "discussion") {
                reorderedContent[4] = part;
              } else {
                if (!reorderedContent[4].component) {
                  reorderedContent[4].component = [];
                }
                reorderedContent[4].component.push(part);
              }
            }
            delete item.component;
          }
          if (Object.keys(item).length > 1) {
            if (!reorderedContent[4].component) {
              reorderedContent[4].component = [];
            }
            reorderedContent[4].component.push(item);
          }
          break;
        case "evidence":
          reorderedContent[5] = item;
          break;
        case "net-effect":
          reorderedContent[6] = item;
          break;
        case "judgments":
          reorderedContent[7] = item;
          break;
        case "competing-interests":
          reorderedContent[8] = item;
          break;
        default:
          alert("Unrecognized content found. JSON includes " + JSON.stringify(item));
          reorderedContent.push(item);
      }
    } else {
      alert("Untyped content found. Search JSON for " + JSON.stringify(item));
    }
  }
  return reorderedContent;
}

const reorderRecommendationRatingContent = (content) => {
  let reorderedContent = [
    {
      "type": sectionCodeRatingSystem
    },
    {
      "type": sectionCodeStrengthOfRecommendation
    },
    {
      "type": sectionCodeDirectionOfRecommendation
    },
    {
      "type": sectionCodeRatings
    },
    {
      "type": sectionCodeCertaintyOfEvidence
    }
  ];
  for (const item of content) {
    const typeCode = item.type?.coding?.[0]?.code;
    if (typeCode) {
      switch (typeCode) {
        case "rating-system":
          reorderedContent[0] = item;
          break;
        case "strength-of-recommendation":
          reorderedContent[1] = item;
          break;
        case "direction-of-recommendation":
          reorderedContent[2] = item;
          break;
        case "ratings":
          reorderedContent[3] = item;
          break;
        case "certainty-of-evidence":
          reorderedContent[4] = item;
          break;
        case "recommendation-rating":
          if (item.component) {
            for (const part of item.component) {
              const partCode = part.type?.coding?.[0]?.code;
              if (partCode === "rating-system") {
                reorderedContent[0] = part;
              } else if (partCode === "strength-of-recommendation") {
                reorderedContent[1] = part;
              } else if (partCode === "direction-of-recommendation") {
                reorderedContent[2] = part;
              } else if (partCode === "ratings") {
                reorderedContent[3] = part;
              } else if (partCode === "certainty-of-evidence") {
                reorderedContent[4] = part;
              } else {
                reorderedContent.push(part);
              }
            }
            delete item.component;
          }
          if (Object.keys(item).length > 1) {
            reorderedContent.push(item);
          }
          break;
        default:
          alert("Unrecognized content found. JSON includes " + JSON.stringify(item));
          reorderedContent.push(item);
      }
    } else {
      alert("Untyped content found. Search JSON for " + JSON.stringify(item));
    }
  }
  return reorderedContent;
}

const recommendationJustificationTextViewChangesToJson = (fhirJson, recommendationJustificationState) => {
  if (fhirJson === undefined) {
    return;
  }
  fhirJson.resourceType = "ArtifactAssessment";
  fhirJson.meta = recommendationJustificationState.meta;
  fhirJson.language = recommendationJustificationState.language;
  fhirJson.text = recommendationJustificationState.text;
  fhirJson.contained = recommendationJustificationState.contained || "DELETEME";
  fhirJson.extension = recommendationJustificationState.extension || "DELETEME";
  fhirJson.modifierExtension = recommendationJustificationState.modifierExtension || "DELETEME";
  fhirJson.language = recommendationJustificationState.language || "DELETEME";
  fhirJson.identifier = recommendationJustificationState.identifier || "DELETEME";
  fhirJson.title = recommendationJustificationState.title || "DELETEME";
  fhirJson.date = recommendationJustificationState.date || "DELETEME";
  fhirJson.copyright = recommendationJustificationState.copyright || "DELETEME";
  fhirJson.approvalDate = recommendationJustificationState.approvalDate || "DELETEME";
  fhirJson.lastReviewDate = recommendationJustificationState.lastReviewDate || "DELETEME";
  fhirJson.artifactReference = recommendationJustificationState.artifactReference || "DELETEME";
  fhirJson.artifactCanonical = recommendationJustificationState.artifactCanonical || "DELETEME";
  fhirJson.artifactUri = recommendationJustificationState.artifactUri || "DELETEME";
  fhirJson.content = recommendationJustificationState.content || "DELETEME"

  deleteEmptyElementsInObjectRecursive(fhirJson);
  let [citationSummary, citationJson, fhirJsonWithCiteAs] = generateCitation(fhirJson, fhirJson.id);
  fhirJson = fhirJsonWithCiteAs;
  return fhirJson;
}

const recommendationRatingTextViewChangesToJson = (fhirJson, recommendationRatingState) => {
  if (fhirJson === undefined) {
    return;
  }
  fhirJson.resourceType = "ArtifactAssessment";
  fhirJson.meta = recommendationRatingState.meta;
  fhirJson.language = recommendationRatingState.language;
  fhirJson.text = recommendationRatingState.text;
  fhirJson.contained = recommendationRatingState.contained || "DELETEME";
  fhirJson.extension = recommendationRatingState.extension || "DELETEME";
  fhirJson.modifierExtension = recommendationRatingState.modifierExtension || "DELETEME";
  fhirJson.language = recommendationRatingState.language || "DELETEME";
  fhirJson.identifier = recommendationRatingState.identifier || "DELETEME";
  fhirJson.title = recommendationRatingState.title || "DELETEME";
  fhirJson.date = recommendationRatingState.date || "DELETEME";
  fhirJson.copyright = recommendationRatingState.copyright || "DELETEME";
  fhirJson.approvalDate = recommendationRatingState.approvalDate || "DELETEME";
  fhirJson.lastReviewDate = recommendationRatingState.lastReviewDate || "DELETEME";
  fhirJson.artifactReference = recommendationRatingState.artifactReference || "DELETEME";
  fhirJson.artifactCanonical = recommendationRatingState.artifactCanonical || "DELETEME";
  fhirJson.artifactUri = recommendationRatingState.artifactUri || "DELETEME";
  fhirJson.content = recommendationRatingState.content || "DELETEME"

  deleteEmptyElementsInObjectRecursive(fhirJson);
  let [citationSummary, citationJson, fhirJsonWithCiteAs] = generateCitation(fhirJson, fhirJson.id);
  fhirJson = fhirJsonWithCiteAs;
  return fhirJson;
}

const createNewClassificationJson = (recommendationJustificationState) => {
  let contentArray = recommendationJustificationState.newClassifications;
  if (!contentArray) {
    return "";
  }
  let classifiedResourceType = "ArtifactAssessment";
  let classifiedFoi = recommendationJustificationState.id;
  let classifiedReference = classifiedResourceType + "/" + classifiedFoi;
  let classifedTitle = recommendationJustificationState.title || "[Untitled.]";
  let classificationJson = {
    "resourceType": "ArtifactAssessment",
    "artifactReference": {
      "reference": classifiedReference,
      "type": classifiedResourceType,
      "display": classifedTitle
    },
    "content": contentArray,
    "meta": {
      "profile": ["http://hl7.org/fhir/uv/ebm/StructureDefinition/classification"]
    }
  };
  return classificationJson;
}

const builderUpdateRecommendationJustificationJson = (resourceId, fhirEntryState, formInputsStateRef) => {
  let recommendationJustificationState = formInputsStateRef.current.recommendationJustificationState;

  let fhirJson = jsonErrorCheck2("ArtifactAssessment", resourceId, fhirEntryState.startingVersionId, fhirEntryState.fhirEntryString);
  if (fhirJson === undefined || fhirJson === false) {
    return false;
  }


  if (fhirEntryState.activeIndex == 0) {
    fhirJson = recommendationJustificationTextViewChangesToJson(fhirJson, recommendationJustificationState);
    fhirJson = jsonErrorCheck2("ArtifactAssessment", resourceId, fhirEntryState.startingVersionId, fhirJson);
    if (fhirJson === undefined || fhirJson === false) {
      return false;
    }
  }
  let newClassificationJson = createNewClassificationJson(recommendationJustificationState);

  return [fhirJson, newClassificationJson];
}

const builderUpdateRecommendationRatingJson = (resourceId, fhirEntryState, formInputsStateRef) => {
  let recommendationRatingState = formInputsStateRef.current.recommendationRatingState;

  let fhirJson = jsonErrorCheck2("ArtifactAssessment", resourceId, fhirEntryState.startingVersionId, fhirEntryState.fhirEntryString);
  if (fhirJson === undefined || fhirJson === false) {
    return false;
  }


  if (fhirEntryState.activeIndex == 0) {
    fhirJson = recommendationRatingTextViewChangesToJson(fhirJson, recommendationRatingState);
    fhirJson = jsonErrorCheck2("ArtifactAssessment", resourceId, fhirEntryState.startingVersionId, fhirJson);
    if (fhirJson === undefined || fhirJson === false) {
      return false;
    }
  }
  let newClassificationJson = createNewClassificationJson(recommendationRatingState);

  return [fhirJson, newClassificationJson];
}
export { builderUpdateRecommendationJustificationJson, builderUpdateRecommendationRatingJson, 
  reorderRecommendationJustificationContent, reorderRecommendationRatingContent }